import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * Tiered quantity cards that drive the native quantity-selector-component.
 *
 * This component owns no quantity state of its own. The stock quantity block stays in the form,
 * hidden, and remains the single source of truth: it keeps `name="quantity"` for FormData and
 * `ref="quantitySelector"` for product-form-component. These cards only read and write it via its
 * public API, so max validation, variant re-snapping and cart sync all stay stock.
 *
 * @extends {Component}
 */
class WomQuantitySelector extends Component {
  // Guards the write -> event -> re-read cycle from looping.
  #syncing = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    // Native value can change without us: variant swap re-snapping, cart sync, quantity rules.
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleNativeUpdate);

    // Derive from the native input rather than trusting server-rendered markup, so repeat
    // connections after a morph or Section Rendering re-render land in the right state.
    this.#syncFromNative();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleNativeUpdate);
  }

  /** @returns {any} The native quantity selector driving this form, if present. */
  get #native() {
    return this.closest('.product-form-buttons')?.querySelector('quantity-selector-component') ?? null;
  }

  /** @returns {HTMLElement[]} */
  get #options() {
    return Array.from(this.querySelectorAll('[data-wom-qty-option]'));
  }

  /** @returns {HTMLElement | null} */
  get #selected() {
    return this.querySelector('[data-wom-qty-option].is-selected');
  }

  /**
   * The quantity an option currently represents. Custom options read their stepper.
   * @param {HTMLElement} option
   * @returns {number}
   */
  #quantityOf(option) {
    if (option.dataset.womQtyCustom === 'true') {
      const value = option.querySelector('[data-wom-qty-value]');
      return parseIntOrDefault(value?.textContent?.trim(), this.#floorOf(option));
    }

    return parseIntOrDefault(option.dataset.womQtyOption, 1);
  }

  /**
   * @param {HTMLElement} option
   * @returns {number}
   */
  #floorOf(option) {
    return parseIntOrDefault(option.dataset.womQtyOption, 1);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const stepButton = target.closest('[data-wom-qty-step]');
    if (stepButton instanceof HTMLElement) {
      // Keep the step from bubbling into a card re-selection.
      event.preventDefault();
      event.stopPropagation();
      this.#step(stepButton, parseIntOrDefault(stepButton.dataset.womQtyStep, 1));
      return;
    }

    const option = target.closest('[data-wom-qty-option]');
    if (option instanceof HTMLElement && this.contains(option)) this.#select(option);
  };

  /**
   * Arrow keys move between cards, space/enter picks one.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const option = target.closest('[data-wom-qty-option]');
    if (!(option instanceof HTMLElement) || !this.contains(option)) return;

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.#select(option);
      return;
    }

    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
    if (!forward && !backward) return;

    const options = this.#options;
    const index = options.indexOf(option);
    if (index === -1) return;

    event.preventDefault();
    const next = options[(index + (forward ? 1 : -1) + options.length) % options.length];
    if (!next) return;

    this.#select(next);
    next.focus();
  };

  /**
   * Marks a card selected and pushes its quantity to the native selector.
   * @param {HTMLElement} option
   */
  #select(option) {
    this.#markSelected(option);

    const applied = this.#writeToNative(this.#quantityOf(option));
    // The native selector's min/max wins, so reflect what actually landed.
    if (option.dataset.womQtyCustom === 'true') this.#setStepperDisplay(option, applied);
  }

  /**
   * @param {HTMLElement} option
   * @param {number} quantity
   */
  #setStepperDisplay(option, quantity) {
    const display = option.querySelector('[data-wom-qty-value]');
    if (display) display.textContent = String(quantity);
  }

  /**
   * Visual + a11y state only. Does not touch the native selector.
   * @param {HTMLElement | null} option
   */
  #markSelected(option) {
    const options = this.#options;

    for (const candidate of options) {
      const isSelected = candidate === option;
      candidate.classList.toggle('is-selected', isSelected);
      candidate.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      // Roving tabindex so the group is one tab stop.
      candidate.tabIndex = isSelected ? 0 : -1;
    }

    // With nothing selected every card would be tabindex -1 and the group unreachable by keyboard.
    if (!option && options[0]) options[0].tabIndex = 0;
  }

  /**
   * @param {HTMLElement} button
   * @param {number} direction
   */
  #step(button, direction) {
    const option = button.closest('[data-wom-qty-option]');
    const value = button.closest('[data-wom-qty-stepper]')?.querySelector('[data-wom-qty-value]');
    if (!(option instanceof HTMLElement) || !value) return;

    const floor = this.#floorOf(option);
    const max = this.#native?.getEffectiveMax?.() ?? null;

    let next = parseIntOrDefault(value.textContent?.trim(), floor) + direction;
    if (next < floor) next = floor;
    if (max !== null && next > max) next = Math.max(floor, max);

    // Stepping an unselected card selects it, matching the Dawn behaviour.
    this.#markSelected(option);
    // Write first, then display what actually landed — the native min/max can clamp further.
    this.#setStepperDisplay(option, this.#writeToNative(next));
  }

  /**
   * @param {number} quantity
   * @returns {number} The value the native selector actually accepted.
   */
  #writeToNative(quantity) {
    const native = this.#native;
    if (!native) return quantity;

    const { min } = native.getCurrentValues?.() ?? { min: 1 };
    const max = native.getEffectiveMax?.() ?? null;
    const clamped = Math.max(min, max !== null ? Math.min(quantity, max) : quantity);

    this.#syncing = true;
    native.setValue?.(String(clamped));
    native.updateButtonStates?.();

    // Let the rest of the theme hear about it exactly as if the stepper had been used.
    if (typeof native.onQuantityChange === 'function') {
      native.onQuantityChange();
    } else {
      native.dispatchEvent(new QuantitySelectorUpdateEvent(clamped));
    }
    this.#syncing = false;

    return clamped;
  }

  #handleNativeUpdate = (/** @type {Event} */ event) => {
    if (this.#syncing) return;
    if (!(event.target instanceof Element) || !this.closest('.product-form-buttons')?.contains(event.target)) {
      return;
    }

    this.#syncFromNative();
  };

  /** Highlights the card matching the native input's current value. */
  #syncFromNative() {
    const native = this.#native;
    if (!native) return;

    const { value } = native.getCurrentValues?.() ?? { value: 0 };
    if (!value) return;

    const options = this.#options;
    const exact = options.find(
      (option) => option.dataset.womQtyCustom !== 'true' && this.#floorOf(option) === value
    );

    if (exact) {
      this.#markSelected(exact);
      return;
    }

    // No fixed tier matches, so fall to the custom option if the value is within its range.
    const custom = options.find((option) => option.dataset.womQtyCustom === 'true');
    if (custom && value >= this.#floorOf(custom)) {
      const display = custom.querySelector('[data-wom-qty-value]');
      if (display) display.textContent = String(value);
      this.#markSelected(custom);
      return;
    }

    this.#markSelected(this.#selected);
  }
}

if (!customElements.get('wom-quantity-selector-component')) {
  customElements.define('wom-quantity-selector-component', WomQuantitySelector);
}
